import {faArrowRight, faCheck, faCircleXmark, faLoader} from '@fortawesome/pro-duotone-svg-icons';
import {faXmark} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Card, CardContent, CardHeader, Divider, MenuItem, Stack, Typography} from '@mui/material';
import Button from '@mui/material/Button';
import {FC, useMemo, useState} from 'react';
import {useFormContext, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {Field} from '@minimal/components/hook-form';
import {CONNECTOR_TYPES, ConnectorType} from '@pages/pilotage-rh/accounts/account-types';

interface Props {
  error?: boolean;
}

const stateIcon = {
  ok: faCheck,
  nok: faCircleXmark,
  processing: faLoader,
};
interface ConnecteurDTO {
  success: boolean | null;
  nbSteps: number;
  steps: string[];
}

export const ConnectorForm: FC<Props> = ({error}) => {
  const {t} = useTranslation();
  const {setValue, control} = useFormContext();

  const connectorData = useWatch({control, name: 'connectorDSN'});
  const connectorType: ConnectorType | '' = connectorData?.type ?? '';

  const [testResult, setTestResult] = useState<ConnecteurDTO | null>(null);
  const handleTypeChange = (type: ConnectorType | '') => {
    if (type === '') {
      setValue('connectorDSN', null, {shouldDirty: true, shouldValidate: true});
    } else if (type === 'silae') {
      setValue(
        'connectorDSN',
        {
          type: 'silae',
          config: {
            clientId: ' ',
            clientSecret: ' ',
            idConfig: ' ',
            mainKey: ' ',
          },
          periodStart: new Date().toISOString(),
          periodEnd: null,
        },
        {shouldDirty: true, shouldValidate: true},
      );
    }
  };

  const testConfiguration = () => {
    setTestResult({success: null, nbSteps: 0, steps: []} as ConnecteurDTO);
    fetch('/api/config/dsn-connector-tester', {
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('token'),
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(connectorData),
    })
      .then((response) => response.json())
      .then((dto) => setTestResult(dto));
  };

  const testButtonIcon = useMemo(() => {
    if (testResult === null) {
      return faArrowRight;
    }
    if (testResult.success === null) {
      return faLoader;
    }
    return testResult.success ? faCheck : faCircleXmark;
  }, [testResult]);

  return (
    <Card style={{maxWidth: '600px'}}>
      <CardHeader title={t('pages.pilotage-rh.account.fields.connector')} />
      <CardContent>
        <Stack divider={<Divider />} spacing={3}>
          {error && (
            <Typography variant="body2" color="error.main">
              {t('pages.pilotage-rh.account.notifications.connector.error')}
            </Typography>
          )}

          <Field.Select
            id="connector-type"
            name="connector-type-select"
            label={t('pages.pilotage-rh.account.connectorDsn.type')}
            value={connectorType}
            onChange={(e) => handleTypeChange(e.target.value as ConnectorType | '')}
          >
            <MenuItem value="">{t('pages.pilotage-rh.account.connectorDsn.types.none')}</MenuItem>
            {CONNECTOR_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {t(`pages.pilotage-rh.account.connectorDsn.types.${type}`)}
              </MenuItem>
            ))}
          </Field.Select>

          {connectorType === 'silae' && (
            <Stack spacing={2}>
              <Field.Text
                name="connectorDSN.config.clientId"
                label={t('pages.pilotage-rh.account.connectorDsn.silae.clientId')}
                fullWidth
                margin="none"
              />
              <Field.Text
                name="connectorDSN.config.clientSecret"
                label={t('pages.pilotage-rh.account.connectorDsn.silae.clientSecret')}
                fullWidth
                margin="none"
              />
              <Field.Text
                name="connectorDSN.config.idConfig"
                label={t('pages.pilotage-rh.account.connectorDsn.silae.idConfig')}
                fullWidth
                margin="none"
              />
              <Field.Text
                name="connectorDSN.config.mainKey"
                label={t('pages.pilotage-rh.account.connectorDsn.silae.mainKey')}
                fullWidth
                margin="none"
              />
            </Stack>
          )}
          {connectorType !== '' && (
            <Stack spacing={2}>
              <Field.DatePicker
                name="connectorDSN.periodStart"
                label={t('pages.pilotage-rh.account.connectorDsn.periodStart')}
                format="DD/MM/YYYY"
              />
              <Field.DatePicker
                name="connectorDSN.periodEnd"
                label={t('pages.pilotage-rh.account.connectorDsn.periodEnd')}
                format="DD/MM/YYYY"
              />
            </Stack>
          )}

          {connectorType && (
            <>
              {testResult && (
                <ul>
                  {testResult.steps.map((v, k) => (
                    <li key={k}>
                      {`${k + 1} - ${v}`}
                      <FontAwesomeIcon
                        icon={testResult.success || k !== testResult.steps.length - 1 ? faCheck : faXmark}
                        size={'lg'}
                        //color={testResult.success ? 'success' : 'error'}
                        style={{position: 'absolute', right: '2rem', width: '2rem'}}
                      />
                    </li>
                  ))}
                </ul>
              )}
              <Button
                variant="soft"
                onClick={() => testConfiguration()}
                disabled={testResult !== null && testResult.success === null}
                color={
                  testResult && testResult?.success !== null ? (testResult.success ? 'success' : 'error') : 'inherit'
                }
              >
                {t('pages.pilotage-rh.account.connectorDsn.testMe')}
                <FontAwesomeIcon icon={testButtonIcon} size={'lg'} color={'secondary'} style={{marginLeft: '2rem'}} />
              </Button>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
